import React from "react";
import { Chip, SvgIcon } from "@mui/material";
import type { ChipProps, SxProps, Theme } from "@mui/material";
import { OrderStatus } from "client/api-v2";

// A ring, drawn once and shared by all five glyphs.
const ring = <circle cx="12" cy="12" r="9" />;

/**
 * One distinct shape per status. A list row shows several chips at once and the same five theme
 * colours appear on all of them, so the ring alone is not enough: it gains a dot, a check, a cross
 * or a clock hand. Five tints of one shape would make the glyph decorative rather than a second
 * reading of the status. The shapes are the prototype's own.
 */
const statusGlyphs: Record<OrderStatus, { name: string; shape: React.ReactNode }> = {
  [OrderStatus.New]: { name: "ring", shape: ring },
  [OrderStatus.Quote]: {
    name: "ring-dot",
    shape: (
      <>
        {ring}
        <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
      </>
    ),
  },
  [OrderStatus.Accepted]: {
    name: "ring-check",
    shape: (
      <>
        {ring}
        <path d="M8 12.5l2.5 2.5L16 9" />
      </>
    ),
  },
  [OrderStatus.Cancelled]: {
    name: "ring-cross",
    shape: (
      <>
        {ring}
        <path d="M9 9l6 6M15 9l-6 6" />
      </>
    ),
  },
  [OrderStatus.Expired]: {
    name: "ring-clock",
    shape: (
      <>
        {ring}
        <path d="M12 7v5l3.5 2" />
      </>
    ),
  },
};

// Colour by meaning, from the theme's own semantic slots: an open quote is informational, an
// accepted order succeeded, a cancelled one failed, an expired one lapsed, and a new draft carries
// no signal yet.
const statusColors: Record<OrderStatus, ChipProps["color"]> = {
  [OrderStatus.New]: "default",
  [OrderStatus.Quote]: "info",
  [OrderStatus.Accepted]: "success",
  [OrderStatus.Cancelled]: "error",
  [OrderStatus.Expired]: "warning",
};

/**
 * The status of an order or quote, shown the same way on every screen. Status and sales motion are
 * separate fields that both carry a value named "New", so this chip never renders the motion.
 * @param root0
 * @param root0.status
 * @param root0.sx
 */
export function OrderStatusChip({ status, sx }: { status: OrderStatus; sx?: SxProps<Theme> }) {
  const { name, shape } = statusGlyphs[status];

  return (
    <Chip
      label={status}
      color={statusColors[status]}
      size="small"
      variant="outlined"
      sx={sx}
      icon={
        // The label beside it already names the status, so the glyph is decorative to a screen
        // reader. `data-glyph` names the shape for the test that proves the five stay distinct.
        <SvgIcon
          viewBox="0 0 24 24"
          fontSize="small"
          aria-hidden="true"
          data-testid={`order-status-glyph-${status}`}
          data-glyph={name}
          sx={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}
        >
          {shape}
        </SvgIcon>
      }
    />
  );
}
